// src/components/dashboard/history/IncidentHistoryView.js

import { useMemo, useState } from "react";
import { Brain, Download, X } from "lucide-react";
import OperationsSummary from "../OperationsSummary";
import HistoryTable from "./HistoryTable";
import IncidentReportView from "../../workspace/IncidentReportView";

/**
 * Shared body for ClientHistory / HelperHistory. Both pages fetch their own
 * role-specific Firestore data (client history vs. helper history live under
 * different rules) but rendered identically — this used to be ~600 lines
 * duplicated almost verbatim in each page, one in blue, one in purple.
 */

function formatDate(ts) {
  if (!ts) return "N/A";
  try {
    if (typeof ts.toDate === "function") {
      return ts.toDate().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    if (ts.seconds) {
      return new Date(ts.seconds * 1000).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    return new Date(ts).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "N/A";
  }
}

function DetailModal({ task, counterpartyLabel, counterpartyField, onClose, onViewReport, onDelete }) {
  if (!task) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">{task.title || "Incident details"}</h2>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4 text-sm">
            <div>
              <h3 className="mb-1 font-medium text-slate-700">Description</h3>
              <p className="text-slate-600">{task.description || "No description provided."}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="mb-1 font-medium text-slate-700">Domain</h3>
                <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">
                  {task.domain || "General"}
                </span>
              </div>
              <div>
                <h3 className="mb-1 font-medium text-slate-700">Status</h3>
                <span
                  className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                    task.status === "unresolved"
                      ? "border-amber-200 bg-amber-50 text-amber-700"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {task.status === "unresolved" ? "Unresolved" : "Completed"}
                </span>
              </div>
            </div>

            {task[counterpartyField] && (
              <div>
                <h3 className="mb-1 font-medium text-slate-700">{counterpartyLabel}</h3>
                <p className="text-slate-600">{task[counterpartyField]}</p>
              </div>
            )}

            <div>
              <h3 className="mb-1 font-medium text-slate-700">Closed</h3>
              <p className="text-slate-600">{formatDate(task.dateCompleted)}</p>
            </div>

            {task.attachment && (
              <div>
                <h3 className="mb-1 font-medium text-slate-700">Attachment</h3>
                {task.attachment.startsWith("data:image") ? (
                  <img
                    src={task.attachment}
                    alt="Attachment"
                    className="mt-2 max-h-64 rounded-lg border border-slate-200 object-contain"
                  />
                ) : (
                  <a
                    href={task.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    View file
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Close
            </button>
            {task.incidentReportId && (
              <button
                type="button"
                onClick={() => onViewReport(task.incidentReportId)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
              >
                View resolution report
              </button>
            )}
            <button
              type="button"
              onClick={() => onDelete(task.id)}
              className="rounded-lg border border-rose-300 px-4 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-50"
            >
              Remove from history
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportOverlay({ reportId, onClose }) {
  if (!reportId) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="my-8 w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Resolution report</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <IncidentReportView reportId={reportId} />
      </div>
    </div>
  );
}

export default function IncidentHistoryView({
  tasks,
  stats,
  domains,
  counterpartyLabel,
  counterpartyField,
  onDelete,
  onExport,
  emptyAction,
}) {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedDomain, setSelectedDomain] = useState("all");
  const [selectedTask, setSelectedTask] = useState(null);
  const [reportViewId, setReportViewId] = useState(null);

  const filteredTasks = useMemo(
    () =>
      tasks.filter((task) => {
        if (selectedFilter === "completed" && task.status !== "completed") return false;
        if (selectedFilter === "unresolved" && task.status !== "unresolved") return false;
        if (selectedDomain !== "all" && task.domain !== selectedDomain) return false;
        return true;
      }),
    [tasks, selectedFilter, selectedDomain]
  );

  const topDomain =
    stats.domainBreakdown && Object.keys(stats.domainBreakdown).length > 0
      ? Object.entries(stats.domainBreakdown).sort((a, b) => b[1] - a[1])[0][0]
      : null;

  return (
    <>
      <OperationsSummary
        title="Track record"
        subtitle="Closed and unresolved incidents you've been part of."
        metrics={[
          { id: "total", label: "Total incidents", value: stats.totalTasks },
          {
            id: "rating",
            label: "Average rating",
            value: stats.avgRating > 0 ? stats.avgRating.toFixed(1) : "—",
          },
          {
            id: "domain",
            label: "Most common domain",
            value: topDomain || "—",
          },
        ]}
      />

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="completed">Completed</option>
                <option value="unresolved">Unresolved</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Domain</label>
              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All domains</option>
                {domains.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={onExport}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </section>

      <HistoryTable
        tasks={filteredTasks}
        counterpartyField={counterpartyField}
        onOpen={setSelectedTask}
        onViewReport={setReportViewId}
        onDelete={onDelete}
        emptyTitle="No history yet"
        emptyBody={
          emptyAction ? (
            <>
              Your completed and unresolved incidents will appear here.{" "}
              <button type="button" onClick={emptyAction.onClick} className="text-blue-600 hover:underline">
                {emptyAction.label}
              </button>
            </>
          ) : (
            "Your completed and unresolved incidents will appear here."
          )
        }
      />

      {stats.totalTasks > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <Brain className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <div className="text-sm text-slate-700">
              <p className="font-medium text-slate-900">Pattern</p>
              <p className="mt-0.5">
                {topDomain
                  ? `Most of your incidents fall under ${topDomain}.`
                  : "Keep closing incidents to see patterns here."}
              </p>
            </div>
          </div>
        </section>
      )}

      <DetailModal
        task={selectedTask}
        counterpartyLabel={counterpartyLabel}
        counterpartyField={counterpartyField}
        onClose={() => setSelectedTask(null)}
        onViewReport={(id) => {
          setSelectedTask(null);
          setReportViewId(id);
        }}
        onDelete={(id) => {
          onDelete(id);
          setSelectedTask(null);
        }}
      />

      <ReportOverlay reportId={reportViewId} onClose={() => setReportViewId(null)} />
    </>
  );
}
